import { supabase } from '@/features/sistema/services/supabaseClient';

import { getThreadById } from './commentThreadsApi';

const COMMENTS_TABLE = 'app_comments';
const CREATE_COMMENT_RPC = 'fn_app_comment_create';

export type AppCommentRecord = {
  id: string;
  thread_id: string;
  parent_id: string | null;
  conteudo: string | null;
  tipo: string | null;
  arquivo_url: string | null;
  arquivo_nome: string | null;
  mencoes: string[] | null;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  created_by: string;
  comentario_pai_id?: string | null;
};

export async function listCommentsByThread(
  threadId: string,
  options: { limit?: number; offset?: number } = {},
) {
  if (!threadId) {
    return [] as AppCommentRecord[];
  }

  const { limit, offset } = options;

  let query = supabase
    .from(COMMENTS_TABLE)
    .select('*')
    .eq('thread_id', threadId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });

  if (limit != null) {
    const start = offset ?? 0;
    const end = start + Math.max(limit - 1, 0);
    query = query.range(start, end);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data ?? []) as AppCommentRecord[];
}

export type CreateCommentInput = {
  threadId?: string;
  recurso?: string;
  recursoId?: string;
  obraId?: string;
  conteudo: string;
  tipo?: string;
  arquivoUrl?: string | null;
  arquivoNome?: string | null;
  arquivo_url?: string | null;
  arquivo_nome?: string | null;
  mencoes?: string[] | null;
  parentId?: string | null;
  comentario_pai_id?: string | null;
  userId?: string | null;
  createdBy?: string | null;
  created_by?: string | null;
};

export async function createComment(input: CreateCommentInput) {
  const {
    threadId,
    conteudo,
    tipo = 'texto',
    arquivoUrl,
    arquivoNome,
    arquivo_url,
    arquivo_nome,
    mencoes = null,
    parentId,
    comentario_pai_id,
    userId,
    createdBy,
    created_by,
  } = input;

  let recurso = input.recurso ?? null;
  let recursoId = input.recursoId ?? null;
  let obraId = input.obraId ?? null;

  if ((!recurso || !recursoId || !obraId) && threadId) {
    const thread = await getThreadById(threadId);
    if (!thread) {
      throw new Error(`commentsApi.createComment: thread ${threadId} não encontrado`);
    }
    recurso = thread.recurso;
    recursoId = thread.recurso_id;
    obraId = thread.obra_id;
  }

  if (!recurso || !recursoId || !obraId) {
    throw new Error(
      'commentsApi.createComment: é preciso informar (recurso, recursoId, obraId) ou então um threadId válido',
    );
  }

  const finalArquivoUrl = arquivoUrl ?? arquivo_url ?? null;
  const finalArquivoNome = arquivoNome ?? arquivo_nome ?? null;
  const finalParent = parentId ?? comentario_pai_id ?? null;
  const finalUserId = userId ?? createdBy ?? created_by ?? null;

  const { data, error } = await supabase
    .rpc(CREATE_COMMENT_RPC, {
      p_recurso: recurso,
      p_recurso_id: recursoId,
      p_obra_id: obraId,
      p_conteudo: conteudo,
      p_tipo: tipo,
      p_arquivo_url: finalArquivoUrl,
      p_arquivo_nome: finalArquivoNome,
      p_mencoes: mencoes,
      p_parent_id: finalParent,
      p_user_id: finalUserId,
    })
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return data as AppCommentRecord;
}

